import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

interface StatementPayment {
  payment_number: string;
  payment_date: string;
  payment_method: string;
  amount: number;
}

interface StatementRefund {
  refund_date: string;
  amount: number;
  reason: string | null;
}

interface StatementInvoice {
  id: number;
  invoice_number: string;
  issue_date: string;
  status: string;
  invoice_total: number;
  amount_paid: number;
  total_refunds: number;
  balance_due: number;
  payments: StatementPayment[];
  refunds: StatementRefund[];
}

interface Statement {
  customer: {
    id: number;
    company_name: string;
    contact_name: string;
    email: string | null;
  };
  period: { key: string; periodStart: string; periodEnd: string; label: string; isSingleMonth: boolean };
  outstanding: StatementInvoice[];
  settledInPeriod: StatementInvoice[];
  totalDue: number;
}

interface CustomerOption {
  id: number;
  company_name: string;
}

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatDate = (value: string) => {
  if (!value) return '';
  const iso = value.slice(0, 10).split('-');
  return `${iso[2]}/${iso[1]}/${iso[0].slice(2)}`;
};

// Chrome only opens the month picker from the small calendar icon. showPicker()
// opens it from a click anywhere in the field. Optional-called because Firefox
// has no month picker at all — there it stays a plain text input, as before.
const openMonthPicker = (e: React.MouseEvent<HTMLInputElement>) => {
  const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
  try {
    input.showPicker?.();
  } catch {
    // Throws outside a user gesture or in a cross-origin frame; the field still works.
  }
};

const humanise = (value: string) =>
  (value || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const StatementView: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasPermission, hasRole } = useAuth();
  const { formatCurrency } = useSettings();

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [customerId, setCustomerId] = useState<string>('');
  const [fromMonth, setFromMonth] = useState<string>(currentMonthKey());
  const [toMonth, setToMonth] = useState<string>(currentMonthKey());

  const [statement, setStatement] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = hasPermission('customer_statements.read') || hasRole(['super_admin', 'admin']);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers');
        if (response.data.success) {
          const list: CustomerOption[] = response.data.data.map((c: any) => ({
            id: c.id,
            company_name: c.company_name
          }));
          list.sort((a, b) => a.company_name.localeCompare(b.company_name));
          setCustomers(list);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error loading customers');
      }
    };
    fetchCustomers();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!customerId) {
      setError('Select a customer first');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/statements/${customerId}?from=${fromMonth}&to=${toMonth}`);
      if (response.data.success) {
        setStatement(response.data.data);
      } else {
        setError('Could not build the statement');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error generating statement');
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [customerId, fromMonth, toMonth]);

  const handleDownloadPDF = async () => {
    if (!customerId) {
      setError('Select a customer first');
      return;
    }
    try {
      setPdfLoading(true);
      setError(null);
      const response = await api.get(`/statements/${customerId}/pdf?from=${fromMonth}&to=${toMonth}`, {
        responseType: 'blob',
        timeout: 60000
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Name from the selected customer, not the previewed one — the request above
      // uses customerId, so reading `statement` here would mislabel the file
      // whenever the dropdown has moved on since the last Generate.
      const selected = customers.find((c) => String(c.id) === customerId);
      const name = selected?.company_name || 'customer';
      // Same sanitising as the backend's Content-Disposition name, including the
      // trim — several customer names end in a space or bracket.
      const safeName = name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const periodKey = fromMonth === toMonth ? fromMonth : `${fromMonth}_${toMonth}`;
      link.download = `statement-${safeName}-${periodKey}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading statement PDF:', err);
      setError('Error generating statement PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const renderInvoiceBlock = (invoice: StatementInvoice) => (
    <React.Fragment key={invoice.id}>
      <TableRow sx={{ '& td': { borderBottom: 'none', fontWeight: 600 } }}>
        <TableCell>{formatDate(invoice.issue_date)}</TableCell>
        <TableCell>{invoice.invoice_number}</TableCell>
        <TableCell />
        <TableCell align="right">{formatCurrency(invoice.invoice_total)}</TableCell>
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
      </TableRow>

      {invoice.payments.map((payment) => (
        <TableRow key={`p-${invoice.id}-${payment.payment_number}`} sx={{ '& td': { borderBottom: 'none', color: 'text.secondary' } }}>
          <TableCell sx={{ pl: 4 }}>{formatDate(payment.payment_date)}</TableCell>
          <TableCell>{payment.payment_number}</TableCell>
          <TableCell>{humanise(payment.payment_method)}</TableCell>
          <TableCell align="right" />
          <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
          <TableCell align="right" />
          <TableCell align="right" />
        </TableRow>
      ))}

      {invoice.refunds.map((refund, index) => (
        <TableRow key={`r-${invoice.id}-${index}`} sx={{ '& td': { borderBottom: 'none', color: 'text.secondary' } }}>
          <TableCell sx={{ pl: 4 }}>{formatDate(refund.refund_date)}</TableCell>
          <TableCell>Refund</TableCell>
          <TableCell>{refund.reason || ''}</TableCell>
          <TableCell align="right" />
          <TableCell align="right" />
          <TableCell align="right">{formatCurrency(refund.amount)}</TableCell>
          <TableCell align="right" />
        </TableRow>
      ))}

      <TableRow>
        <TableCell colSpan={6} />
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(invoice.balance_due)}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );

  if (!canView) {
    return (
      <Box>
        <Alert severity="warning">You do not have permission to view customer statements.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>
          Statements
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Monthly statement of account showing outstanding invoices with their payments and refunds.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto auto' },
            gap: 2,
            alignItems: 'center'
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="statement-customer-label">Customer</InputLabel>
            <Select
              labelId="statement-customer-label"
              label="Customer"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value as string);
                setStatement(null);
              }}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={String(customer.id)}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From"
            type="month"
            size="small"
            value={fromMonth}
            onChange={(e) => {
              const value = e.target.value;
              setFromMonth(value);
              // Keep the range valid rather than rejecting it after the fact
              if (value > toMonth) setToMonth(value);
              setStatement(null);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ onClick: openMonthPicker }}
            fullWidth
          />

          <TextField
            label="To"
            type="month"
            size="small"
            value={toMonth}
            onChange={(e) => {
              const value = e.target.value;
              setToMonth(value);
              if (value < fromMonth) setFromMonth(value);
              setStatement(null);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ onClick: openMonthPicker }}
            fullWidth
          />

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={handleGenerate}
            disabled={loading || !customerId}
          >
            Generate
          </Button>

          <Button
            variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
            onClick={handleDownloadPDF}
            disabled={pdfLoading || !customerId}
          >
            Download PDF
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !statement && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {statement && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="h6">{statement.customer.company_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {statement.customer.contact_name}
                {statement.customer.email ? ` · ${statement.customer.email}` : ''}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="subtitle1">{statement.period.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                As at {formatDate(statement.period.periodEnd)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell align="right">Invoice</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Refund</TableCell>
                  <TableCell align="right">Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.outstanding.length === 0 && statement.settledInPeriod.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      No invoice activity for this period.
                    </TableCell>
                  </TableRow>
                )}

                {statement.outstanding.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>
                      OUTSTANDING INVOICES
                    </TableCell>
                  </TableRow>
                )}
                {statement.outstanding.map(renderInvoiceBlock)}

                {statement.settledInPeriod.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>
                      {statement.period.isSingleMonth ? 'SETTLED THIS MONTH' : 'SETTLED IN THIS PERIOD'}
                    </TableCell>
                  </TableRow>
                )}
                {statement.settledInPeriod.map(renderInvoiceBlock)}

                <TableRow>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    TOTAL BALANCE DUE
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {formatCurrency(statement.totalDue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${statement.outstanding.length} outstanding`} />
            <Chip size="small" label={`${statement.settledInPeriod.length} settled ${statement.period.isSingleMonth ? 'this month' : 'in this period'}`} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default StatementView;
